import apiClient from '../utils/apiClient';
import { Product } from 'shared/models';

const API_URL = '/api/products';

// Product service for public product data

const productService = {
  // Get all products with optional pagination and filtering
  getProductsByPage: async (page = 1, limit = 20, filter = {}) => {
    const params = {
      page,
      limit,
      ...filter
    };
    
    try {
      console.log('Fetching products with params:', params);
      const response = await apiClient.get(API_URL, { params });
      console.log('Products response:', response.data);
      
      // Return structured response with pagination info and Product instances
      return {
        products: (response.data.products || []).map(productData => new Product(productData)),
        currentPage: response.data.currentPage || page,
        totalPages: response.data.totalPages || 1,
        totalProducts: response.data.totalProducts || 0
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  },
  
  // Get product by ID with its insert sets
  getProductById: async (productId) => {
    try {
      console.log(`Fetching product by ID: ${productId}`);
      const response = await apiClient.get(`${API_URL}/${productId}`);
      console.log('Product by ID response:', response.data);
      
      if (!response.data.product) {
        console.error('No product found in API response:', response.data);
        throw new Error('Product not found');
      }
      
      // Convert to Product instance
      return new Product(response.data.product);
    } catch (error) {
      console.error(`Error fetching product with ID ${productId}:`, error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error; // Rethrow so the component can handle it
    }
  },
  
  // Get recently added products
  getRecentlyAddedProducts: async (limit = 5) => {
    try {
      console.log('Fetching recent products with limit:', limit);
      const response = await apiClient.get(`${API_URL}/recent`, { params: { limit } });
      console.log('Recent products response:', response.data);
      
      // Validate that products array exists
      if (!response.data.products || !Array.isArray(response.data.products)) {
        console.error('Invalid products data structure:', response.data);
        return [];
      }
      
      // Log if any products are missing slugs
      response.data.products.forEach(product => {
        if (!product.slug) {
          console.warn('Product missing slug:', product);
        }
      });
      
      // Convert to array of Product instances
      return response.data.products.map(productData => new Product(productData));
    } catch (error) {
      console.error('Error fetching recent products:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      return []; // Return empty array instead of throwing to prevent component failures
    }
  },
  
  // Search products with pagination
  searchProductsByPage: async (term, page = 1, limit = 20) => {
    const params = {
      term,
      page,
      limit
    };
    
    const response = await apiClient.get(`${API_URL}/search`, { params });
    
    return {
      products: (response.data.products || []).map(productData => new Product(productData)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalProducts: response.data.totalProducts || 0
    };
  },
  
  // Get products by year with pagination
  getProductsByYearByPage: async (year, page = 1, limit = 20) => {
    const params = {
      page,
      limit
    };
    
    const response = await apiClient.get(`${API_URL}/year/${year}`, { params });
    
    return {
      products: (response.data.products || []).map(productData => new Product(productData)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalProducts: response.data.totalProducts || 0
    };
  },
  
  // Get products by publisher with pagination
  getProductsByPublisherByPage: async (publisher, page = 1, limit = 20) => {
    const params = {
      page,
      limit
    };
    
    const response = await apiClient.get(`${API_URL}/publisher/${publisher}`, { params });
    
    return {
      products: (response.data.products || []).map(productData => new Product(productData)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalProducts: response.data.totalProducts || 0
    };
  },
  
  // Get multiple products by IDs
  getProductsByIds: async (productIds) => {
    if (!productIds || !productIds.length) {
      return [];
    }
    
    // Sanitize input: Ensure all IDs are valid MongoDB ObjectIDs (24-character hex strings)
    const validIds = productIds.filter(id => 
      typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id)
    );
    
    if (validIds.length === 0) {
      console.warn('No valid product IDs provided to getProductsByIds');
      return [];
    }
    
    try {
      // Use POST request with body instead of query params to safely handle multiple IDs
      const response = await apiClient.post(`${API_URL}/batch`, { ids: validIds });
      
      if (!response.data.products || !Array.isArray(response.data.products)) {
        console.error('Invalid products data structure:', response.data);
        return [];
      }
      
      // Convert to array of Product instances
      return response.data.products.map(productData => new Product(productData));
    } catch (error) {
      console.error('Error fetching products by IDs:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      return []; // Return empty array instead of throwing to prevent component failures
    }
  },
  
  // Only public methods here - admin methods moved to productAdminService
};

export default productService;